<template>
  <form method="post" @submit.prevent="send" novalidate>
    <div class="modal-card">
      <header class="modal-card-head">
        <p class="modal-card-title is-centered">
          {{ $t('attributes.add') + $t('attributes.travel_group') }}
        </p>
      </header>
      <section class="modal-card-body">
        <form-wrapper :validator="$v.form">
          <b-field grouped>
            <b-field expanded>
              <template #label>
                {{ $t('attributes.product') }}
                <!-- eslint-disable-next-line-->
                <span v-if="selectedProductName" v-html="': ' + selectedProductName"></span>
              </template>
              <form-group name="product_code" expanded>
                <b-autocomplete
                  ref="product_code_input"
                  v-model="form.product_code"
                  name="product_code"
                  field="code"
                  :data="products"
                  :loading="isLoadingProducts"
                  :placeholder="$t('message.please_input_product_code')"
                  :open-on-focus="false"
                  @typing="loadProducts"
                  @select="onSelectProduct"
                >
                  <template #default="props">
                    <p>
                      {{ props.option.code }} -
                      <!-- eslint-disable-next-line-->
                      <span v-html="props.option.name"></span>
                    </p>
                  </template>
                  <template #empty>
                    {{
                      form.product_code
                        ? isLoadingProducts
                          ? $t('attributes.searching')
                          : $t('attributes.not_found')
                        : $t('message.please_input_product_code')
                    }}
                  </template>
                </b-autocomplete>
              </form-group>
            </b-field>

            <form-group
              name="serial"
              attribute="attributes.group_serial"
              :label="$t('attributes.group_serial')"
            >
              <b-select
                v-model="form.serial"
                name="serial"
                :placeholder="
                  $t('message.please_select') + $t('attributes.group_serial')
                "
                expanded
              >
                <option
                  v-for="option in group_serials"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>
          </b-field>

          <form-group
            name="departure_dates"
            attribute="attributes.departure_date"
            :label="$t('attributes.departure_date')"
          >
            <b-field>
              <b-input
                v-model="form.departure_dates"
                name="departure_date"
                readonly
                expanded
              ></b-input>
              <p class="control">
                <button class="button is-danger" @click.prevent="onClearDates">
                  Clear
                </button>
              </p>
            </b-field>
          </form-group>

          <b-field>
            <v-date-picker
              v-model="selectedDates"
              mode="multiple"
              :attributes="attrs"
              :min-date="min_date"
              :columns="2"
              :step="1"
              color="indigo"
              is-expanded
              is-inline
              @input="onSelectDepartureDates"
            />
          </b-field>
        </form-wrapper>
      </section>
      <footer class="modal-card-foot">
        <b-button :loading="isSending" @click="$parent.close()">
          {{ $t('attributes.cancel') }}
        </b-button>
        <b-button
          :loading="isSending || isLoading"
          type="is-primary"
          native-type="submit"
        >
          {{ $t('attributes.add') }}
        </b-button>
      </footer>
    </div>
  </form>
</template>

<script>
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'
import { PRODUCTS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'CreateForm',

  props: {
    isSending: {
      type: Boolean,
      default: true
    }
  },
  data() {
    const minDate = this.$dayjs()
      .startOf('day')
      .toDate()

    return {
      isLoadingProducts: false,
      products: [],
      selectedProductName: null,

      group_serials: ['A', 'B', 'C', 'D', 'E', 'F'],

      min_date: minDate,
      attrs: [
        {
          key: 'today',
          highlight: 'teal',
          dates: new Date()
        }
      ],
      selectedDates: [],

      form: {
        product_code: null,
        serial: 'A',
        departure_dates: ''
      }
    }
  },
  validations: {
    form: {
      product_code: { required },
      serial: { required },
      departure_dates: { required }
    }
  },
  computed: {
    isLoading() {
      return this.isLoadingProducts
    }
  },
  mounted() {
    this.$refs.product_code_input.focus()
  },
  methods: {
    async loadAsyncProducts() {
      const api = `${PRODUCTS_API}/list`

      const params = {
        code: this.form.product_code
      }

      this.isLoadingProducts = true
      try {
        const res = await this.axios.get(api, { params })
        this.products = res.data.data
      } catch (e) {
        this.products = []

        notifyService.fetch_error('product', e)
      } finally {
        this.isLoadingProducts = false
      }
    },
    loadProducts: debounce(function() {
      this.loadAsyncProducts()
    }, 500),
    onSelectProduct(option) {
      this.selectedProductName = option && option.name
    },
    onSelectDepartureDates() {
      this.form.departure_dates = [...this.selectedDates]
        .sort((d1, d2) => d1 - d2)
        .map(d => this.formatDate(d))
        .join(', ')
    },
    onClearDates() {
      this.selectedDates = []
      this.form.departure_dates = ''
    },
    send() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('onSend', this.form)
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-card {
  min-height: 480px;
}
</style>
